'use client';

import { getHomePageData } from '@/api/home';
import useEffectWithCurReady from '@/hooks/useEffectWithCurReady';
import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box } from '@mui/material';
import Image from '@/components/Image';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

// 类型定义
interface WorkDetail {
  image: {
    material?: string[];
    video?: string[];
    videoCover?: string[];
  };
  image_order: string[];
  ambassador: {
    full_name: string;
  };
}

interface AmbassadorItem {
  ambassador_id: string;
  work_detail: WorkDetail;
}

interface PageData {
  title: string;
  bg_color: string;
  btn_word: string;
  btn_word_color: string;
  btn_bg: string;
}

interface Props {
  pageData: PageData;
  sDPageExp: (
    mkt_name: string,
    pits_index: number,
    pits_jump_type?: string,
    pits_jump_content?: string,
  ) => void;
  deviceType: EPlatform;
}

// 单个大使项组件 - 用于独立的曝光检测
function AmbassadorItem({
  item,
  index,
  deviceType,
  onExposure,
  onDetailClick,
}: {
  item: AmbassadorItem;
  index: number;
  deviceType: EPlatform;
  onExposure: (index: number) => void;
  onDetailClick: (item: AmbassadorItem, index: number) => void;
}) {
  // 每个大使单独的曝光检测
  const [expRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 曝光埋点
  useEffect(() => {
    if (inView) {
      onExposure(index);
    }
  }, [inView, index, onExposure]);

  // 获取展示图片
  const getImage = (ambassadorItem: AmbassadorItem): string => {
    try {
      if (!ambassadorItem?.work_detail?.image || !ambassadorItem?.work_detail?.image_order?.length)
        return '';

      const { image, image_order } = ambassadorItem.work_detail;
      const getFirstImage = (type: string) => {
        if (type === 'material' && image.material?.length) return image.material[0];
        if (type === 'video' && image.video?.length) return image.videoCover?.[0] || '';
        return '';
      };

      // 按优先级顺序尝试获取图片
      return (
        getFirstImage(image_order[0]) || // 尝试第一优先级
        getFirstImage(image_order[1]) || // 尝试第二优先级
        ''
      ); // 如果都没有返回空字符串
    } catch (error) {
      logger.error('getImage error:', error);
      return '';
    }
  };

  // 获取alt文本
  const getAlt = (ambassadorItem: AmbassadorItem): string => {
    const str = ambassadorItem?.work_detail?.ambassador?.full_name || '';
    return `ZEELOOL brand ambrassador ${str}`;
  };

  return (
    <Box
      ref={expRef}
      component="li"
      className="w-full h-full cursor-pointer"
      onClick={() => onDetailClick(item, index)}
    >
      <Image
        src={getImage(item)}
        alt={getAlt(item)}
        width={deviceType !== EPlatform.WEB ? 150 : 200}
        height={deviceType !== EPlatform.WEB ? 194 : 435}
        className="w-full h-[194px] lg:h-[300px] 2xl:h-[435px] object-cover"
      />
    </Box>
  );
}

export default function AmbassadorPart({ pageData, sDPageExp, deviceType }: Props) {
  const [ambassadorList, setAmbassadorList] = useState<AmbassadorItem[]>([]);
  const router = useRouter();
  // ✅ 使用新的埋点系统
  const { trackEvent } = useReporting();

  // 获取大使列表
  useEffectWithCurReady(() => {
    getAmbassadorList();
  }, []);

  const getAmbassadorList = async () => {
    try {
      const params = {
        block_type: 301,
        status: 1, // 使用线上数据
      };
      const res = await getHomePageData(params);

      if (res.code === 200) {
        // 根据Vue项目的实际数据结构处理
        const list = (res.data as any)?.list?.[0]?.ambassadorList || [];
        // 移动端只显示4个，桌面端显示全部
        const displayList = deviceType !== EPlatform.WEB ? list.slice(0, 4) : list;
        setAmbassadorList(displayList);
      } else {
        logger.error('获取大使列表失败:', res.code);
        setAmbassadorList([]);
      }
    } catch (error) {
      logger.error('获取大使列表失败:', error);
      setAmbassadorList([]);
    }
  };

  // 处理单个大使的曝光
  const handleAmbassadorExposure = (index: number) => {
    sDPageExp('大使风采展示', index);
  };

  // ✅ 详情点击处理 - 使用新的埋点系统
  const handleDetailClick = (item: AmbassadorItem, index: number) => {
    try {
      // 点击埋点
      const sdEventParams = {
        mkt_name: '大使风采展示',
        pits_index: index,
      };

      trackEvent(
        {
          eventName: 'AmbassadorHomePageMkt_Click',
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('AmbassadorHomePageMkt_Click tracking error:', error);
    }

    // 跳转到详情页
    router.push(`/ambassador-detail/${item.ambassador_id}`);
  };

  // ✅ 查看更多按钮点击 - 使用新的埋点系统
  const handleViewMoreClick = () => {
    try {
      // 按钮点击埋点
      const sdEventParams = {
        mkt_name: '大使风采展示',
        btn_name: pageData.btn_word,
      };

      trackEvent(
        {
          eventName: 'AmbassadorHomePageMktBtn_Click',
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('AmbassadorHomePageMktBtn_Click tracking error:', error);
    }

    // 跳转到列表页
    router.push('/ambassador-list');
  };

  // 安全检查：确保pageData存在
  if (!pageData) {
    return null;
  }

  // 如果没有大使数据，不显示组件
  if (!ambassadorList.length) {
    return null;
  }

  return (
    <Box
      className="w-full px-[24px] py-[35px] lg:px-[60px] lg:py-[50px] 2xl:px-[96px] 2xl:py-[104px]"
      style={{ backgroundColor: pageData.bg_color }}
    >
      <Box>
        <h3
          className="font-bb text-[24px] leading-[26px] text-center lg:text-[36px] lg:leading-[44px] 2xl:text-[80px] 2xl:leading-[80px] text-primary-white lg:px-[120px] 2xl:px-[241px]"
          dangerouslySetInnerHTML={{ __html: pageData.title }}
        />

        <Box
          component="ul"
          className="grid grid-cols-2 lg:grid-cols-5 2xl:grid-cols-5 gap-[0px] lg:gap-[16px] 2xl:gap-[24px] my-[20px] lg:my-[32px] 2xl:my-[64px]"
        >
          {ambassadorList.map((item, index) => (
            <AmbassadorItem
              key={index}
              item={item}
              index={index}
              deviceType={deviceType}
              onExposure={handleAmbassadorExposure}
              onDetailClick={handleDetailClick}
            />
          ))}
        </Box>

        <Box className="text-center">
          <button
            onClick={handleViewMoreClick}
            className="min-w-[192px] lg:min-w-[300px] 2xl:min-w-[352px] px-[12px] lg:px-[24px] 2xl:px-[24px] py-[10px] lg:py-[12px] 2xl:py-[20px] rounded-[20px] lg:rounded-[30px] 2xl:rounded-[42px] font-bb text-[14px] leading-[17px] lg:text-[16px] lg:leading-[19px] 2xl:text-[20px] 2xl:leading-[24px] transition-colors hover:opacity-90 h-[40px] lg:h-[50px] 2xl:h-[80px] cursor-pointer"
            style={{
              backgroundColor: pageData.btn_bg,
              color: pageData.btn_word_color,
            }}
          >
            {pageData.btn_word}
          </button>
        </Box>
      </Box>
    </Box>
  );
}
